import { useState } from "react";
import { useProductGroups, ProductGroupFormData, ProductGroup } from "@/features/produtos/hooks/useProductGroups";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Ban } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function ProductGroups() {
  const { groups, isLoading, createGroup, updateGroup, deleteGroup } = useProductGroups();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ProductGroup | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ProductGroupFormData>({
    descricao: "",
    referencia: "",
    isInativo: false,
    mostrarPdv: false,
    mostrarCelular: false,
    mostrarCatalogo: false,
    mostrarEcommerce: false,
  });

  const [busca, setBusca] = useState("");

  const groupsFiltrados = busca
    ? groups.filter(g =>
        g.descricao?.toLowerCase().includes(busca.toLowerCase()) ||
        (g.referencia ?? "").toLowerCase().includes(busca.toLowerCase()))
    : groups;

  const paginacao = usePaginacaoLocal(groupsFiltrados);

  const handleOpenDialog = (group?: ProductGroup) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        descricao: group.descricao || "",
        referencia: group.referencia || "",
        isInativo: group.isInativo,
        mostrarPdv: group.mostrarPdv,
        mostrarCelular: group.mostrarCelular,
        mostrarCatalogo: group.mostrarCatalogo,
        mostrarEcommerce: group.mostrarEcommerce,
        idPos: group.idPos,
      });
    } else {
      setEditingGroup(null);
      setFormData({ descricao: "", referencia: "", isInativo: false, mostrarPdv: false, mostrarCelular: false, mostrarCatalogo: false, mostrarEcommerce: false });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingGroup) {
      await updateGroup.mutateAsync({ id: editingGroup.id, ...formData });
    } else {
      await createGroup.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Grupos de Produtos</h1>
          <p className="text-muted-foreground">Gerencie os grupos de produtos</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Grupo
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição ou referência..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead className="text-center">PDV</TableHead>
              <TableHead className="text-center">E-commerce</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((group) => (
              <TableRow key={group.id}>
                <TableCell className="font-medium">{group.descricao || "-"}</TableCell>
                <TableCell>{group.referencia || "-"}</TableCell>
                <TableCell className="text-center">{group.mostrarPdv ? "✓" : "-"}</TableCell>
                <TableCell className="text-center">{group.mostrarEcommerce ? "✓" : "-"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={group.isInativo ? "secondary" : "success"}>
                    {group.isInativo ? "Inativo" : "Ativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(group)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!group.isInativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(group.id)}>
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum grupo encontrado para esta busca." : "Nenhum grupo cadastrado"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{editingGroup ? "Editar Grupo" : "Novo Grupo"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="referencia">Referência</Label>
              <Input
                id="referencia"
                value={formData.referencia}
                onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="idPos">ID POS</Label>
              <Input
                id="idPos"
                value={formData.idPos || ""}
                onChange={(e) => setFormData({ ...formData, idPos: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="mostrarPdv"
                  checked={formData.mostrarPdv}
                  onCheckedChange={(checked) => setFormData({ ...formData, mostrarPdv: checked })}
                />
                <Label htmlFor="mostrarPdv">Mostrar PDV</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="mostrarCelular"
                  checked={formData.mostrarCelular}
                  onCheckedChange={(checked) => setFormData({ ...formData, mostrarCelular: checked })}
                />
                <Label htmlFor="mostrarCelular">Mostrar Celular</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="mostrarCatalogo"
                  checked={formData.mostrarCatalogo}
                  onCheckedChange={(checked) => setFormData({ ...formData, mostrarCatalogo: checked })}
                />
                <Label htmlFor="mostrarCatalogo">Mostrar Catálogo</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="mostrarEcommerce"
                  checked={formData.mostrarEcommerce}
                  onCheckedChange={(checked) => setFormData({ ...formData, mostrarEcommerce: checked })}
                />
                <Label htmlFor="mostrarEcommerce">Mostrar E-commerce</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="ativo"
                  checked={!formData.isInativo}
                  onCheckedChange={(v) => setFormData({ ...formData, isInativo: !v })}
                />
                <Label htmlFor="ativo">Ativo</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar grupo</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar este grupo de produto?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { deleteGroup.mutate(inativarId); setInativarId(null); } }}>
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
